import { useGetLimitedPartnerUsersPermissionView } from "@vega-api/generated/coinvest-deal-permission/coinvest-deal-permission";
import type { CoinvestPartner } from "@vega-api/generated/types/coinvestPartner";
import { Spinner } from "@vega-core/components/Loaders/LoadingSpinner/Spinner";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

type ContactsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  limitedPartner: CoinvestPartner;
  dealId: string;
};

export const ContactsModal = ({
  isOpen,
  onClose,
  limitedPartner,
  dealId,
}: ContactsModalProps) => {
  const { data, isLoading, isError } = useGetLimitedPartnerUsersPermissionView(
    dealId,
    limitedPartner.id,
    {
      query: { enabled: isOpen },
    },
  );

  const lpUsers = data?.data?.users ?? [];

  const description = (
    <span className="body-base-regular text-text/secondary">
      {m.coInvestments_contacts_modal_description({
        lpName: limitedPartner.name,
      })}
    </span>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-8">
          <Spinner className="w-8 h-8" />
        </div>
      );
    }

    if (isError) {
      return (
        <p className="body-base-regular text-text/negative">
          {m.errors_generic()}
        </p>
      );
    }

    if (lpUsers.length > 0) {
      return (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {lpUsers.map((user) => (
            <div
              className={cnTwMerge(
                "flex flex-col py-3",
                "border-b border-border/subtle last:border-0",
              )}
              data-testid={`contact-${user.id}`}
              key={user.id}
            >
              <span className="body-base-medium text-text/primary text-left">
                {user.fullName}
              </span>
              <span className="body-sm-regular text-text/secondary text-left">
                {user.email}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return (
      <p
        className="body-base-regular text-text/secondary"
        data-testid="contacts-modal-empty"
      >
        {m.coInvestments_contacts_modal_empty()}
      </p>
    );
  };

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainAction: onClose,
        mainActionText: m.btn_close(),
      }}
      dataTestId="contacts-modal"
      description={description}
      open={isOpen}
      setOpen={onClose}
      title={m.coInvestments_contacts_modal_title({
        lpName: limitedPartner.name,
      })}
    >
      {renderContent()}
    </ActionaryModal>
  );
};
